import React from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
interface WithChildren {
  children: React.ReactNode;
  className?: string;
}
export function Container({
  children,
  className = ''
}: WithChildren) {
  return <div className={`mx-auto w-full max-w-[1240px] px-5 sm:px-8 ${className}`} data-id="element-385" data-mp="1grzpjrwgpioe">{children}</div>;
}
interface SectionProps extends WithChildren {
  tone?: 'white' | 'mist' | 'dark' | 'champagne';
  id?: string;
  'aria-labelledby'?: string;
}
const tones: Record<NonNullable<SectionProps['tone']>, string> = {
  white: 'bg-white',
  mist: 'bg-mist',
  dark: 'surface-dark text-white',
  champagne: 'bg-champagne'
};
export function Section({
  children,
  className = '',
  tone = 'white',
  id,
  ...rest
}: SectionProps) {
  return <section id={id} className={`${tones[tone]} py-16 sm:py-24 ${className}`} {...rest} data-id="element-386" data-mp="24hk7c7gvq9w3">
      <Container data-id="element-387" data-mp="29izrzyf5aedy">{children}</Container>
    </section>;
}
export function Kicker({
  children,
  className = ''
}: WithChildren) {
  return <span className={`inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold ${className}`} data-id="element-388" data-mp="35l47rujzurd5">
      {children}
    </span>;
}
interface SectionHeaderProps {
  kicker?: string;
  title: string;
  lead?: string;
  id?: string;
  align?: 'left' | 'centre';
  reversed?: boolean;
}
export function SectionHeader({
  kicker,
  title,
  lead,
  id,
  align = 'left',
  reversed = false
}: SectionHeaderProps) {
  return <header className={`${align === 'centre' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}`} data-id="element-389" data-mp="3oaand8phr2uq">
      {kicker && <Kicker data-id="element-390" data-mp="3jd2clt6oa13i">{kicker}</Kicker>}
      <h2 id={id} className={`mt-3 font-display text-[28px] leading-tight sm:text-[36px] ${reversed ? 'text-white' : 'text-ink'}`} data-id="element-391" data-mp="3jevc8oivbl20">
        {title}
      </h2>
      {lead && <p className={`mt-4 text-[17px] ${reversed ? 'text-white/72' : 'text-body'}`} data-id="element-392" data-mp="ax8w3ecccpmg">{lead}</p>}
    </header>;
}
export function Card({
  children,
  className = ''
}: WithChildren) {
  return <div className={`rounded-md border border-line bg-white p-6 sm:p-7 ${className}`} data-id="element-393" data-mp="20db6dyqnpxra">{children}</div>;
}
export function Pill({
  children,
  className = ''
}: WithChildren) {
  return <span className={`inline-flex items-center rounded-full border border-line bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted ${className}`} data-id="element-394" data-mp="1qob528oomq9x">
      {children}
    </span>;
}
export function Callout({
  children,
  className = ''
}: WithChildren) {
  return <div className={`border-l-2 border-gold bg-champagne p-5 text-[14px] text-body ${className}`} data-id="element-395" data-mp="1mm6fpm59oft3">
      {children}
    </div>;
}
export function PullQuote({
  children,
  className = ''
}: WithChildren) {
  return <blockquote className={`border-l-2 border-gold pl-6 font-display text-[22px] leading-snug text-ink sm:text-[26px] ${className}`} data-id="element-396" data-mp="1cngupki2qf8m">
      {children}
    </blockquote>;
}
interface RevealProps extends WithChildren {
  delay?: number;
}
export function Reveal({
  children,
  className = '',
  delay = 0
}: RevealProps) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className} data-id="element-397" data-mp="qg4md1ty0549">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut',
    delay
  }} data-id="element-398" data-mp="qho5d06zgu9q">
      {children}
    </motion.div>;
}
interface StatProps {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  label: string;
  source?: string;
  reversed?: boolean;
}
export function StatBlock({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  label,
  source,
  reversed = false
}: StatProps) {
  const reduced = useReducedMotion();
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [display, setDisplay] = React.useState(reduced ? value : 0);
  React.useEffect(() => {
    if (!inView || reduced) {
      if (reduced) setDisplay(value);
      return;
    }
    let frame = 0;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduced, value]);
  return <div ref={ref} data-id="element-399" data-mp="38kuqi672pay9">
      <p className={`font-display text-[40px] leading-none sm:text-[52px] ${reversed ? 'text-white' : 'text-royal'}`} data-id="element-400" data-mp="38mfm7z54izxp">
        {prefix}
        {display.toFixed(decimals)}
        {suffix}
      </p>
      <p className={`mt-3 text-[15px] ${reversed ? 'text-white/75' : 'text-body'}`} data-id="element-401" data-mp="3ida7b06mvthk">{label}</p>
      {source && <p className={`mt-2 text-small ${reversed ? 'text-white/45' : 'text-muted'}`} data-id="element-402" data-mp="2pv68tes8fr0u">{source}</p>}
    </div>;
}